/**
 * Read and write item .pro files, byte-locally.
 *
 * The wire format is `protoRead` in engine/src/proto.cc:1673 and nothing else:
 * three int32s of header (pid, messageId, fid), the OBJ_TYPE_ITEM block, then a
 * per-ItemType subdata block chosen by the `type` field that appeared earlier in
 * the same stream. Every integer is BIG-endian — `fileReadInt32` (db.cc:317)
 * byte-swaps on the way in, and `_db_freadInt` is the same function under
 * another name — and `soundId`/`soundCode` are single raw bytes.
 *
 * The design rule here is that we never *rebuild* a proto. A parse keeps the
 * original buffer; an edit copies it and overwrites exactly the bytes of the
 * fields named. That gives three things at once:
 *
 *   1. Diff locality — `maxDamage: 50` changes four bytes, so the compiled
 *      overlay diffs cleanly and a reviewer can see the whole edit.
 *   2. Unknown-byte preservation — anything past the fields we know about (a
 *      longer proto from a future engine, a mod's padding) survives untouched.
 *   3. No round-trip risk — we cannot introduce the engine's own
 *      min/maxDamage writer transposition (see fields.ts) because we never write
 *      a field we were not asked to write.
 */

import {
  ITEM_TYPES,
  fieldWidth,
  itemLayout,
  itemProtoSize,
  type FieldSpec,
  type ItemTypeName,
} from './fields';

export class ProtoError extends Error {}

export interface ProtoField {
  spec: FieldSpec;
  /** Byte offset of the field within the .pro. */
  offset: number;
}

export interface ItemProtoFile {
  pid: number;
  /** Object type from the pid's high byte. Always 0 (OBJ_TYPE_ITEM) here. */
  objectType: number;
  /** 1-based items.lst line the pid indexes. */
  protoNumber: number;
  messageId: number;
  itemType: ItemTypeName;
  /** The bytes exactly as read. Never mutated. */
  bytes: Uint8Array;
  /** Field name -> {spec, offset}, in wire order. */
  layout: Map<string, ProtoField>;
  /** Bytes past the last known field, if any. Carried through every write. */
  trailingBytes: number;
}

export function objectTypeFromPid(pid: number): number {
  return (pid >> 24) & 0xff;
}

export function protoNumberFromPid(pid: number): number {
  return pid & 0xffffff;
}

export function makePid(objectType: number, protoNumber: number): number {
  return ((objectType & 0xff) << 24) | (protoNumber & 0xffffff);
}

function readI32(b: Uint8Array, o: number): number {
  return ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) | 0;
}

function writeI32(b: Uint8Array, o: number, v: number): void {
  b[o] = (v >>> 24) & 0xff;
  b[o + 1] = (v >>> 16) & 0xff;
  b[o + 2] = (v >>> 8) & 0xff;
  b[o + 3] = v & 0xff;
}

/** Offset of `type` inside the item block — needed before the layout exists. */
const TYPE_FIELD_OFFSET = (() => {
  // pid, messageId, fid, lightDistance, lightIntensity, flags, extendedFlags, sid
  return 8 * 4;
})();

export function parseItemProto(bytes: Uint8Array): ItemProtoFile {
  if (bytes.length < TYPE_FIELD_OFFSET + 4) {
    throw new ProtoError(`Not an item proto: ${bytes.length} bytes is shorter than the common header.`);
  }

  const pid = readI32(bytes, 0);
  const objectType = objectTypeFromPid(pid);
  if (objectType !== 0) {
    throw new ProtoError(
      `This proto is object type ${objectType}, not an item. The recipe format v1 edits items only ` +
        '(critters, scenery and walls use the same machinery and are a later lane).'
    );
  }

  const typeIndex = readI32(bytes, TYPE_FIELD_OFFSET);
  const itemType = ITEM_TYPES[typeIndex];
  if (!itemType) {
    throw new ProtoError(`Unknown ItemType ${typeIndex} — the file is corrupt or is not an item proto.`);
  }

  const expected = itemProtoSize(itemType);
  if (bytes.length < expected) {
    throw new ProtoError(
      `Truncated ${itemType} proto: ${bytes.length} bytes, but the format needs ${expected}.`
    );
  }

  const layout = new Map<string, ProtoField>();
  let offset = 0;
  for (const spec of itemLayout(itemType)) {
    layout.set(spec.name, { spec, offset });
    offset += fieldWidth(spec.kind);
  }

  return {
    pid,
    objectType,
    protoNumber: protoNumberFromPid(pid),
    messageId: readI32(bytes, 4),
    itemType,
    bytes,
    layout,
    trailingBytes: bytes.length - expected,
  };
}

export function hasField(proto: ItemProtoFile, name: string): boolean {
  return proto.layout.has(name);
}

export function getField(proto: ItemProtoFile, name: string): number {
  const f = proto.layout.get(name);
  if (!f) {
    throw new ProtoError(`No field "${name}" on a ${proto.itemType} item. ${nearestFields(proto, name)}`);
  }
  return f.spec.kind === 'u8' ? proto.bytes[f.offset] : readI32(proto.bytes, f.offset);
}

/** Every field of this proto as a plain object — what the editor form binds to. */
export function readAllFields(proto: ItemProtoFile): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [name] of proto.layout) out[name] = getField(proto, name);
  return out;
}

function nearestFields(proto: ItemProtoFile, name: string): string {
  const lower = name.toLowerCase();
  const near = [...proto.layout.keys()].filter((k) => k.toLowerCase().includes(lower.split('.')[0]));
  return near.length ? `Did you mean: ${near.slice(0, 4).join(', ')}?` : `Known fields: ${[...proto.layout.keys()].slice(0, 6).join(', ')}, …`;
}

/**
 * Returns a new buffer with only the named fields overwritten. Fields the caller
 * did not name are copied byte-for-byte, including anything past the known
 * layout.
 */
export function writeItemProto(proto: ItemProtoFile, fields: Record<string, number>): Uint8Array {
  const out = new Uint8Array(proto.bytes);
  for (const [name, value] of Object.entries(fields)) {
    const f = proto.layout.get(name);
    if (!f) throw new ProtoError(`No field "${name}" on a ${proto.itemType} item. ${nearestFields(proto, name)}`);
    if (!f.spec.edit) {
      throw new ProtoError(`Field "${name}" is not editable by a recipe: ${f.spec.why ?? 'read-only'}.`);
    }
    if (!Number.isInteger(value)) {
      throw new ProtoError(`Field "${name}" must be a whole number, got ${value}.`);
    }
    if (f.spec.kind === 'u8') {
      if (value < 0 || value > 255) throw new ProtoError(`Field "${name}" is a single byte; ${value} does not fit.`);
      out[f.offset] = value;
    } else {
      writeI32(out, f.offset, value);
    }
  }
  return out;
}

/** Byte offsets that differ between two buffers. The diff-locality assertion. */
export function changedOffsets(a: Uint8Array, b: Uint8Array): number[] {
  const n = Math.max(a.length, b.length);
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    if (a[i] !== b[i]) out.push(i);
  }
  return out;
}

/**
 * Field-level difference between two serializations of the same proto. This is
 * the inverse direction: given the user's edited workspace, recover the recipe.
 */
export function diffItemProto(before: ItemProtoFile, after: ItemProtoFile): Record<string, number> {
  if (before.itemType !== after.itemType) {
    throw new ProtoError(
      `Cannot diff a ${before.itemType} against a ${after.itemType}: the subdata block is a different shape. ` +
        'A changed item type is a new item, not an edit.'
    );
  }
  const out: Record<string, number> = {};
  for (const [name, f] of before.layout) {
    if (!f.spec.edit) continue;
    const a = getField(before, name);
    const b = getField(after, name);
    if (a !== b) out[name] = b;
  }
  return out;
}

/**
 * Builds a fresh .pro from a template proto — the `newItems` path. The template
 * supplies every byte (including the art and script ids, which is exactly why a
 * new item must be cloned from an existing one rather than conjured); pid and
 * messageId are then repointed at the new proto number.
 */
export function cloneItemProto(
  template: ItemProtoFile,
  opts: { pid: number; messageId: number; fields?: Record<string, number> }
): Uint8Array {
  const out = new Uint8Array(template.bytes);
  writeI32(out, 0, opts.pid);
  writeI32(out, 4, opts.messageId);
  if (!opts.fields) return out;
  const rebased = parseItemProto(out);
  return writeItemProto(rebased, opts.fields);
}

/** Vanilla convention, and what `newItems` allocates: message base = 100 × proto number. */
export function conventionalMessageId(protoNumber: number): number {
  return protoNumber * 100;
}
